import { ComponentProps, ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import logLogInScreenForgotPasswordTapped from '@alltrails/amplitude/events/logLogInScreenForgotPasswordTapped';
import Typography from '@alltrails/core/components/Typography';
import Heading from '@alltrails/denali/components/Heading';
import { ModalAriaLabelledByContext } from '@alltrails/modal';
import Link from '@alltrails/denali/components/Link';
import Logo from '@alltrails/denali/components/Logo';
import LoginResponse from '../../types/LoginResponse';
import AltLoginsForm from '../AltLoginsForm';
import EmailPasswordLogin from '../EmailPasswordLogin';
import TermsOfService from '../TermsOfService';
import styles from './SignUpModal.module.scss';

// Desktop: https://app.zeplin.io/project/6218dc5177101a641929be50/screen/6218e13d32440b60dea65769
// Mobile: https://app.zeplin.io/project/6218dc5177101a641929be50/screen/6218dec5dccf0d54f6dee4f4

type LogInProps = Pick<ComponentProps<typeof AltLoginsForm>, 'appleLoginHandler' | 'facebookLoginHandler' | 'googleLoginHandler'> & {
  grantType?: string | null;
  heading?: ReactNode;
  handleLoginSuccess: ({ user }: LoginResponse) => void;
  prefillEmail?: string;
  isConsolidatedAuth?: boolean;
};

const LogIn = ({ grantType, heading, handleLoginSuccess, prefillEmail, isConsolidatedAuth, ...loginHandlers }: LogInProps) => {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);
  const defaultHeading =
    isConsolidatedAuth && prefillEmail ? (
      <FormattedMessage defaultMessage="Welcome back!" />
    ) : (
      <>
        <FormattedMessage defaultMessage="Welcome back." />
        <br />
        <FormattedMessage defaultMessage="Log in and start exploring." />
      </>
    );

  return (
    <div className={styles.content}>
      {isConsolidatedAuth && prefillEmail && (
        <div className={styles.logo}>
          <Logo variant="symbol" size="lg" color="monochrome-dark" />
        </div>
      )}
      <Heading as="h4" id={ariaLabelledBy} size="400" className={styles.heading}>
        {heading || defaultHeading}
      </Heading>
      <div className={styles.login}>
        <div className={styles.form}>
          <EmailPasswordLogin
            grantType={grantType}
            handleLoginSuccess={handleLoginSuccess}
            prefillEmail={prefillEmail}
            isConsolidatedAuth={isConsolidatedAuth}
          />
        </div>
        <Typography component="div" variant="paragraph200">
          <Link
            href="/users/password/new"
            onClick={() => {
              logLogInScreenForgotPasswordTapped();
            }}
          >
            <FormattedMessage defaultMessage="Forgot your password?" />
          </Link>
        </Typography>
        {!(isConsolidatedAuth && prefillEmail) && <AltLoginsForm {...loginHandlers} authType="login" />}
        <TermsOfService />
      </div>
    </div>
  );
};

export default LogIn;
